import { useMemo, useRef, useState } from 'react';

import { setLocation } from 'js/lib/coursera.redirect';
import { useLocation } from 'js/lib/useRouter';

import type { QUESTION_IDS_TYPE } from 'bundles/onboarding-common/constants/questionScheme';
import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import useClientSideNumberOfItemsPerRow from 'bundles/onboarding-common/hooks/useClientSideNumberOfItemsPerRow';
import useSearchAndSelectOptionState from 'bundles/onboarding-common/hooks/useSearchAndSelectOptionState';
import type { SearchAndSelectOptionWithState, SelectOptionWithState } from 'bundles/onboarding-common/types';

type BaseSelectStateProps<T extends { id: string }> = {
  questionId: QUESTION_IDS_TYPE;
  defaultSingleSelectOption?: SelectOptionWithState<T>;
  getDisplayName: (item: T) => string;
  widthData: {
    itemPadding: number;
    iconWidth: number;
    spacingBetweenIconAndText: number;
  };
  initialState: SearchAndSelectOptionWithState<T>[];
};

const useBaseSelectState = <T extends { id: string }>({
  questionId,
  defaultSingleSelectOption,
  getDisplayName,
  widthData,
  initialState,
}: BaseSelectStateProps<T>) => {
  const containerRef = useRef(null);
  const location = useLocation();

  const {
    items: itemsList,
    dispatch,
    singleSelectOption: iDontKnowOption,
  } = useSearchAndSelectOptionState<T>(initialState, defaultSingleSelectOption);

  const referrer = location.search ? `${location.pathname}${location.search}` : location.pathname;
  const { onboardingUrl } = useBuildOnboardingUrl({ questionId, referrer });

  const [isExpanded, setIsExpanded] = useState(false);

  const itemsToDisplay = itemsList.filter(({ isSelected, isVisible }) => isSelected || isVisible);

  const { numberOfItemsPerRow, shouldUseDefaultLayout, isHydrated } = useClientSideNumberOfItemsPerRow({
    containerRef,
    items: itemsToDisplay.map(getDisplayName),
    pinnedItem: iDontKnowOption ? getDisplayName(iDontKnowOption) : '',
    widthData: {
      spacingBetweenItems: 8,
      itemBorder: 2,
      buttonWidth: 104,
      ...widthData,
    },
    isExpanded,
  });

  const itemsPerRow = useMemo(() => {
    if (!numberOfItemsPerRow) return null;
    let previousRowLastIndex = 0;
    return numberOfItemsPerRow.map((numberOfItems, index) => {
      const isLastRow = index === numberOfItemsPerRow.length - 1;
      const currentRow = itemsToDisplay.slice(previousRowLastIndex, numberOfItems + previousRowLastIndex);

      if (isLastRow && iDontKnowOption) {
        currentRow.push({ ...iDontKnowOption, isSearchResult: false, isVisible: true, isPartOfSearch: false });
      }

      previousRowLastIndex += numberOfItems;

      return currentRow;
    });
  }, [numberOfItemsPerRow, itemsToDisplay, iDontKnowOption]);

  const handleClick = (item: SelectOptionWithState<T>) => {
    if (item.id === iDontKnowOption?.id) {
      if (iDontKnowOption?.isSelected) {
        dispatch({ type: 'DESELECT_SINGLE_SELECT_OPTION' });
      } else {
        dispatch({ type: 'SELECT_SINGLE_SELECT_OPTION' });
      }
      return;
    }

    if (item.isSelected) {
      dispatch({
        type: 'DESELECT_ITEM_BY_ID',
        payload: item.id,
      });
    } else {
      dispatch({
        type: 'SELECT_ITEM_BY_ID',
        payload: item.id,
      });
    }
  };

  const onViewMoreClick = () => {
    // TODO: Add tracking
    setIsExpanded(true);
  };

  const onViewAllClick = () => {
    // TODO: Add tracking
    setLocation(onboardingUrl);
  };

  const isLoading = !isHydrated;

  return {
    itemsList,
    itemsPerRow,
    containerRef,
    handleClick,
    onViewMoreClick,
    onViewAllClick,
    isLoading,
    isExpanded,
    iDontKnowOption,
    shouldUseDefaultLayout,
  };
};

export default useBaseSelectState;
